import * as React from 'react';
import { Ellipsis } from '@common/components/Ellipsis';
import { Flex } from '@common/components/Flex';
import { Button } from '@common/components/Button';
import { MessageConfirmParams } from '@common/hooks/message';
import { I18n } from 'react-redux-i18n';
import { InfoMessage } from '@common/components/info-message';

import './item.styles.scss';

export type TextBlockItem = {
  id: number | string;
  title: string;
};

export type TextBlockProps<Item extends TextBlockItem> = {
  shouldScrollBottom?: boolean;
  data: Item;
  showDeleteConfirmMessage: (props: MessageConfirmParams) => void;
  onEdit: (item: Item) => void;
  onDelete: (item: Item) => void;
};

export function TextBlock<T extends TextBlockItem>(props: TextBlockProps<T>) {
  const { data, onEdit, showDeleteConfirmMessage, onDelete, shouldScrollBottom } = props;
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (shouldScrollBottom) {
      ref.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const handleEditClick = React.useCallback(() => {
    onEdit(data);
  }, [onEdit, data]);

  const handleDeleteClick = React.useCallback(() => {
    if (!onDelete || !showDeleteConfirmMessage) {
      return;
    }

    showDeleteConfirmMessage({
      header: I18n.t('Confirmation Message'),
      content: <InfoMessage>{I18n.t('Are you sure you want to delete this field?')}</InfoMessage>,
      confirmText: I18n.t('Yes'),
      cancelText: I18n.t('No'),
      onCustomOk: () => {
        onDelete(data);
      }
    });
  }, [onDelete, showDeleteConfirmMessage, data]);

  return (
    <Flex reference={ref} width="100%" justify="space-between">
      <Ellipsis text={data.title} width="calc(100% - 60px)" />
      <Flex alignItems="center">
        <Button 
          className="FhqDesignToolTextBlocksItem__action"  
          variant="text" 
          onClick={handleEditClick}
        >
          <i className="fa fa-pencil" />
        </Button>
        <Button 
          className="FhqDesignToolTextBlocksItem__action"
          variant="text"
          onClick={handleDeleteClick}
        >
          <i className="fa fa-trash" />
        </Button>
      </Flex>
    </Flex>
  );
};